// Per-project Plivo numbers. CRM-side endpoint: /api/v1/crm/projects/:id/call-numbers.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { crmApi } from "../api/client";

const key = (projectId) => ["crm", "project-call-numbers", projectId];

export function useProjectCallNumbers(projectId) {
  return useQuery({
    queryKey: key(projectId),
    enabled: !!projectId,
    queryFn: async () => {
      const { data } = await crmApi.get(`/projects/${projectId}/call-numbers`);
      return data?.numbers || [];
    },
    staleTime: 30_000,
  });
}

// Changing a project's Plivo number list affects the call resolver that the
// outbound-call flow uses (which "From" number to use), so we both directly
// patch the local cache (instant UI feedback) AND invalidate any in-progress
// call dial settings the user might be looking at.
function syncCallNumbers(qc, projectId, numbers) {
  qc.setQueryData(key(projectId), numbers);
  qc.invalidateQueries({ queryKey: ["crm", "project-call-numbers", projectId] });
}

export function useAddProjectCallNumber(projectId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const { data } = await crmApi.post(`/projects/${projectId}/call-numbers`, payload);
      return data?.numbers || [];
    },
    onSuccess: (numbers) => syncCallNumbers(qc, projectId, numbers),
  });
}

export function useUpdateProjectCallNumber(projectId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ numberId, ...payload }) => {
      const { data } = await crmApi.patch(`/projects/${projectId}/call-numbers/${numberId}`, payload);
      return data?.numbers || [];
    },
    onSuccess: (numbers) => syncCallNumbers(qc, projectId, numbers),
  });
}

export function useDeleteProjectCallNumber(projectId) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (numberId) => {
      const { data } = await crmApi.delete(`/projects/${projectId}/call-numbers/${numberId}`);
      return data?.numbers || [];
    },
    onSuccess: (numbers) => syncCallNumbers(qc, projectId, numbers),
  });
}
